import { useResponseHandler } from "utils/service/responseHandler";
import {
  ILabelReportsByLabelFullResponse,
  ILabelReportsByLabelParams,
  ILabelReportsByLabelResponse,
  ILabelReportsByLabelResponseInfinite,
  ILabelReportStatistics,
} from "./types";
import { useInfiniteHandler } from "hooks";
import {
  getDefaultInfinitePageParam,
  useInfiniteHandlerWithStatistics,
} from "utils/service/defaultInfinitePageParam";
import {
  DefaultError,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { apiClient } from "utils";
import { IInfinitePageStatisticsResponse } from "services/types";

export const useGetReportLabelStatistics = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ILabelReportStatistics, DefaultError>({
    queryKey: ["ReportLabel-statistics"],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `LabelReport/LabelReports`,
        params: {
          page: 0,
          itemCount: 1,
        },
      });

      const { statistics } =
        await responseHandler<ILabelReportsByLabelFullResponse>(response, {
          groupName: `useGetReportLabelStatistics`,
        });

      return statistics;
    },
  });
};

export const useLabelReportsListInfinite = (
  params?: ILabelReportsByLabelParams
) => {
  const { responseHandler } = useResponseHandler();
  const {
    getNextPageParam,
    infiniteHandlerWithStatistics,
    defaultPageParams,
    statistics,
  } = useInfiniteHandlerWithStatistics<
    ILabelReportsByLabelResponse,
    ILabelReportStatistics
  >(params);

  const query = useInfiniteQuery<ILabelReportsByLabelResponse[], Error>({
    queryKey: ["label-reports-list-infinite", params],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await apiClient({
        method: "GET",
        url: "LabelReport/LabelReports",
        params: {
          ...params,
          ...defaultPageParams,
          page: pageParam,
        },
      });

      const value = await responseHandler<
        IInfinitePageStatisticsResponse<
          ILabelReportsByLabelResponse,
          ILabelReportStatistics
        >
      >(response, {
        groupName: `useGetLabelReportsListInfinite - ${pageParam}`,
      });

      return infiniteHandlerWithStatistics(value);
    },
    initialPageParam: defaultPageParams.page ?? 0,
    getNextPageParam,
  });

  return {
    ...query,
    statistics,
  };
};

export const useGetLabelReportDetailedFile = (
  id: string,
  enabled?: boolean
) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ["labelReport-detailed-file", id],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `LabelReport/DetailedFile/${id}`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};

export const useGetLabelReportFile = (id: string, enabled?: boolean) => {
  const { responseStreamHandler } = useResponseHandler();

  return useQuery<Blob, DefaultError>({
    queryKey: ["labelReport-file", id],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `LabelReport/File/${id}`,
      });

      return await responseStreamHandler(response);
    },
    enabled,
  });
};
